import React from 'react';
import { Box, Card, CardContent, Paper, Typography } from '@mui/material';
// @ts-ignore
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, BarChart, Bar,
} from 'recharts';
import type { CardioProgressSession } from './types';

interface CardioChartsProps {
  cardioProgress: CardioProgressSession[];
}

const CardioCharts: React.FC<CardioChartsProps> = ({ cardioProgress }) => {
  if (cardioProgress.length === 0) return null;

  return (
    <>
      {/* Cardio Statistics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
        {[
          {
            label: 'Дистанция', unit: 'km',
            value: cardioProgress
              .reduce((sum, s) => {
                if (!s.distance) return sum;
                // @ts-ignore
                const dist = typeof s.distance === 'string' ? parseFloat(s.distance) : (s.distance as any);
                return sum + (isNaN(dist) ? 0 : dist);
              }, 0)
              .toFixed(1),
            color: '#42a5f5',
          },
          { label: 'Калории', unit: 'kcal', value: cardioProgress.reduce((sum, s) => sum + (s.caloriesBurned || 0), 0), color: '#ffa726' },
          { label: 'Време', unit: 'мин', value: cardioProgress.reduce((sum, s) => sum + (s.durationMinutes || 0), 0), color: '#66bb6a' },
          { label: 'Сесии', unit: '', value: cardioProgress.length, color: '#ce93d8' },
        ].map((stat) => (
          <Card key={stat.label} sx={{ borderLeft: `3px solid ${stat.color}` }}>
            <CardContent sx={{ textAlign: 'center', p: '12px !important', '&:last-child': { pb: '12px !important' } }}>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {stat.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: stat.color, lineHeight: 1.2, mt: 0.5 }}>
                {stat.value}
              </Typography>
              {stat.unit && (
                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                  {stat.unit}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Charts */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Калории по Време</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={cardioProgress.map(s => ({ date: new Date(s.date).toLocaleDateString('bg-BG'), calories: s.caloriesBurned || 0 }))}
              margin={{ top: 5, right: 5, left: -15, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="calories" stroke="#ff7300" strokeWidth={2} dot={{ r: 4 }} name="Калории" isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Дистанция по Време</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart
              data={cardioProgress.map(s => ({
                date: new Date(s.date).toLocaleDateString('bg-BG'),
                // @ts-ignore
                distance: s.distance ? (typeof s.distance === 'string' ? parseFloat(s.distance) : Number(s.distance)) : 0,
              }))}
              margin={{ top: 5, right: 5, left: -15, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="distance" fill="#82ca9d" name="Дистанция (km)" radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Пулс Тренд</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart
              data={cardioProgress.map(s => ({
                date: new Date(s.date).toLocaleDateString('bg-BG'),
                avg: s.heartRateAvg || 0,
                peak: s.heartRatePeak || 0,
              }))}
              margin={{ top: 5, right: 5, left: -15, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="avg" stroke="#8884d8" strokeWidth={2} name="Среден Пулс" dot={{ r: 4 }} isAnimationActive={false} />
              <Line type="monotone" dataKey="peak" stroke="#ff0000" strokeWidth={2} name="Пиков Пулс" dot={{ r: 4 }} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    </>
  );
};

export default CardioCharts;
